/**
 * Flow Autobuild — status polling + live stream + mutations for the
 * spec → flow pipeline kicked off from `StarterPicker`'s Autobuild mode.
 *
 * Mirrors `useCacheConvergence.ts` for the mutation shape and
 * `useImportStream.ts` (`apps/web/src/hooks/useImportStream.ts`) for the SSE
 * connection/reconnect pattern, adapted to this endpoint's single
 * `flow-autobuild` event name (see
 * `apps/api/src/workflows/routes/workflow-autobuild.routes.ts`).
 *
 * @module ui/pages/FlowDesigner/hooks/useFlowAutobuild
 */
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../../config';
import { toApiError } from '../../../../lib/api-error';

export type FlowAutobuildStage =
  | 'spec_review'
  | 'authoring'
  | 'converging'
  | 'verifying'
  | 'completed';

export type FlowAutobuildOutcome =
  | 'converged'
  | 'unverified'
  | 'blocked'
  | 'error'
  | 'not_testable'
  | 'spec_review_failed'
  | 'canceled';

export type FlowAutobuildStreamEventData = {
  workflowId: string;
  stage: FlowAutobuildStage;
  outcome?: FlowAutobuildOutcome;
  message?: string;
  cycle?: number;
  adhocTestId?: string;
  convergenceRunId?: string;
  ts: string;
};

export type FlowReadiness = {
  convertible: 'yes' | 'with_changes' | 'no';
  blockers: string[];
} | null;

export type FlowAutobuildCounts = {
  testable: number;
  partial: number;
  notTestable: number;
} | null;

export type FlowAutobuildStatus = {
  workflowId: string;
  stage: FlowAutobuildStage;
  outcome?: FlowAutobuildOutcome;
  specReview: {
    adhocTestId: string;
    status: string;
    jobId: string | null;
    flowReadiness: FlowReadiness;
    counts: FlowAutobuildCounts;
  };
  convergence: {
    convergenceRunId: string;
    status: string;
    phase: string | null;
    outcome: string | null;
    cycleCount: number;
  } | null;
};

type AutobuildStatusResponse = { ok: true; autobuild: FlowAutobuildStatus | null };

const TERMINAL_STAGE: FlowAutobuildStage = 'completed';

export function autobuildStatusQueryKey(workflowId: string | null) {
  return ['workflow-autobuild-status', workflowId] as const;
}

/** Polls `GET /workflows/:id/autobuild-status` while the pipeline is non-terminal. */
export function useAutobuildStatus(workflowId: string | null, opts?: { enabled?: boolean }) {
  const enabled = Boolean(workflowId) && (opts?.enabled ?? true);
  return useQuery({
    queryKey: autobuildStatusQueryKey(workflowId),
    enabled,
    refetchInterval: (query) => {
      const data = query.state.data as AutobuildStatusResponse | undefined;
      if (!data?.autobuild) return false;
      return data.autobuild.stage === TERMINAL_STAGE ? false : 2500;
    },
    queryFn: async (): Promise<AutobuildStatusResponse> => {
      if (!workflowId) throw new Error('Missing workflowId');
      const r = await fetch(`${API_URL}/workflows/${workflowId}/autobuild-status`, {
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r, 'Failed to load autobuild status');
      return r.json();
    },
  });
}

/**
 * Live SSE stream for the pipeline. Reconnect-on-mount only (mirrors
 * `useImportStream`) — the route itself replays cached events on connect, so
 * a fresh EventSource always catches up. Every event also invalidates the
 * status query so polling and streaming stay in sync instead of racing.
 */
export function useAutobuildStream(workflowId: string | null, enabled: boolean) {
  const [latestEvent, setLatestEvent] = React.useState<FlowAutobuildStreamEventData | null>(null);
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (!workflowId || !enabled) {
      setLatestEvent(null);
      return;
    }

    const eventSource = new EventSource(`${API_URL}/workflows/${workflowId}/autobuild/stream`, {
      withCredentials: true,
    });

    const handleEvent = (event: MessageEvent) => {
      try {
        const payload = JSON.parse(event.data) as FlowAutobuildStreamEventData;
        setLatestEvent(payload);
        queryClient.invalidateQueries({ queryKey: autobuildStatusQueryKey(workflowId) });
        if (payload.stage === TERMINAL_STAGE) eventSource.close();
      } catch (error) {
        console.error('Failed to parse flow-autobuild event', error);
      }
    };

    eventSource.addEventListener('flow-autobuild', handleEvent as EventListener);
    eventSource.onerror = (err) => {
      console.error('Flow autobuild stream error', err);
      // The route emits a keepalive comment every 5s and closes on terminal
      // stage; a genuine drop just lets the browser's native EventSource
      // retry loop reconnect (default behavior when `onerror` doesn't close).
    };

    return () => {
      eventSource.close();
    };
  }, [workflowId, enabled, queryClient]);

  return latestEvent;
}

export type AutobuildStartPayload = {
  workflowId: string;
  specText: string;
  propertyId: string;
  credentialId?: string | null;
  startUrl?: string | null;
  contextDocumentIds?: string[];
  /** Human-chosen flow name; omitted when the composer's field is blank. */
  name?: string;
};

type AutobuildStartResponse = {
  ok: true;
  workflowId: string;
  adhocTestId: string;
  jobId: string | null;
};

/** POST /workflows/:id/autobuild — starts the spec → flow pipeline. */
export function useAutobuildStartMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AutobuildStartPayload): Promise<AutobuildStartResponse> => {
      const { workflowId, ...body } = payload;
      const r = await fetch(`${API_URL}/workflows/${workflowId}/autobuild`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw await toApiError(r, 'Failed to start Autobuild');
      return r.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: autobuildStatusQueryKey(variables.workflowId) });
      queryClient.invalidateQueries({ queryKey: ['workflow-detail', variables.workflowId] });
    },
  });
}

type AutobuildRetryResponse = { ok: true; convergenceRunId: string; jobId?: string };

/** POST /workflows/:id/autobuild/retry — self-heal a stuck-but-convertible run. */
export function useAutobuildRetryMutation(workflowId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<AutobuildRetryResponse> => {
      if (!workflowId) throw new Error('Missing workflowId');
      const r = await fetch(`${API_URL}/workflows/${workflowId}/autobuild/retry`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r, 'Failed to retry Autobuild');
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: autobuildStatusQueryKey(workflowId) });
    },
  });
}

type AutobuildCancelResponse = { ok: true; phase: 'spec_review' | 'convergence' };

/** POST /workflows/:id/autobuild/cancel — abort an in-flight Autobuild run. */
export function useAutobuildCancelMutation(workflowId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<AutobuildCancelResponse> => {
      if (!workflowId) throw new Error('Missing workflowId');
      const r = await fetch(`${API_URL}/workflows/${workflowId}/autobuild/cancel`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r, 'Failed to cancel Autobuild');
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: autobuildStatusQueryKey(workflowId) });
      queryClient.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
    },
  });
}

/**
 * Combined read model for `AutobuildPipelinePanel`: status query (source of
 * truth, polled while non-terminal) + live stream (fast in-flight updates —
 * message/cycle — that would otherwise wait for the next poll tick).
 */
export function useFlowAutobuild(workflowId: string | null) {
  const statusQuery = useAutobuildStatus(workflowId);
  const autobuild = statusQuery.data?.autobuild ?? null;
  const isActive = Boolean(autobuild);
  const latestEvent = useAutobuildStream(workflowId, isActive);

  return {
    status: autobuild,
    latestEvent,
    isActive,
    isLoading: statusQuery.isLoading,
    outcome: autobuild?.outcome,
    isTerminal: autobuild?.stage === TERMINAL_STAGE,
    refetch: statusQuery.refetch,
  };
}
